/**
 * Hook for the admin-editable Deals field-order tool.
 *
 * Read path: `useDealsFieldLayout()` returns the current ordering
 * for the Deals card field grid. DealsPage consumes this instead of
 * the hardcoded DEALS_FIELD_DEFS constant when the backend has rows.
 *
 * Write path: `useUpdateDealsFieldLayout()` replaces the full
 * ordering (drag-reorder + show/hide). Admin-only on the backend.
 *
 * Migration j8k9l0m1n2o3 seeds the table from the hardcoded FE
 * constant so day-1 state matches the prior hardcoded layout
 * exactly.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '../api/client';

export type DealsFieldZone =
  | 'identity'
  | 'team'
  | 'classification'
  | 'people'
  | 'financials'
  | 'narrative'
  | string;

export type DealsFieldWidth = 'full' | 'half';

export interface DealsFieldLayoutRow {
  field_name: string;
  zone: DealsFieldZone;
  width: DealsFieldWidth;
  display_order: number;
  visible: boolean;
}

export interface DealsFieldLayoutResponse {
  fields: DealsFieldLayoutRow[];
}

const QUERY_KEY = ['deals', 'field-layout'] as const;

export function useDealsFieldLayout() {
  return useQuery<DealsFieldLayoutResponse>({
    queryKey: QUERY_KEY,
    queryFn: () =>
      apiFetch<DealsFieldLayoutResponse>('/api/deals/field-layout'),
    staleTime: 60 * 60 * 1000,
  });
}

export function useUpdateDealsFieldLayout() {
  const qc = useQueryClient();
  return useMutation<
    DealsFieldLayoutResponse,
    Error,
    { fields: DealsFieldLayoutRow[] }
  >({
    mutationFn: (payload) =>
      apiFetch<DealsFieldLayoutResponse>('/api/deals/field-layout', {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}
